/**
 * Minimal transient-toast store.
 *
 * The app had no global notification surface; panic (SD-6) needs one to
 * report what landed ("Panic sent — 48 MIDI msgs → loopMIDI · stopped 2
 * engines"), and pre-flight / guard / docs-sync will reuse it. Deliberately
 * tiny: a signal of active toasts + push/dismiss. `<ToastHost>` renders the
 * stack (mounted inside the shell so it stays iPad-bound per DEC-096).
 */

import { createSignal } from 'solid-js';

export type ToastKind = 'ok' | 'warn' | 'error';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

const [toasts, setToasts] = createSignal<Toast[]>([]);
export { toasts };

let nextId = 1;

/** Default on-screen dwell before auto-dismiss. Errors linger a little
 *  longer than an ok since the operator may need to act on them. */
const DWELL_MS: Record<ToastKind, number> = { ok: 3500, warn: 5000, error: 6000 };

/** Push a toast. Auto-dismisses after a kind-dependent dwell (pass `ms = 0`
 *  to make it sticky). Returns the id so a caller can dismiss early. */
export function pushToast(kind: ToastKind, message: string, ms?: number): number {
  const id = nextId++;
  setToasts((prev) => [...prev, { id, kind, message }]);
  const dwell = ms ?? DWELL_MS[kind];
  if (dwell > 0) {
    setTimeout(() => dismissToast(id), dwell);
  }
  return id;
}

export function dismissToast(id: number): void {
  setToasts((prev) => prev.filter((t) => t.id !== id));
}
